import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { 
  DataTable, 
  Badge, 
  Button, 
  Heading,
  Spinner,
  Alert,
  Select,
  Input
} from '@aidkitorg/component-library';
import { usePost } from '../API';
import { useLocalizedStrings } from '../Localization';

type DistroReviewStatus = 'review_pending' | 'published' | 'closed' | 'approved';

interface DistroReviewListItem {
  id: string;
  title: string;
  status: DistroReviewStatus;
  surveyName: string;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  publishedAt?: string;
  publishedBy?: string;
  publishedByName?: string;
  closedAt?: string;
  closedBy?: string;
  closedByName?: string;
}

interface DistroReviewsListResponse {
  reviews: DistroReviewListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const getStatusBadgeVariant = (status: DistroReviewStatus): 'neutral' | 'error' | 'warning' | 'success' | 'info' => {
  switch (status) {
    case 'published':
      return 'success';
    case 'approved':
      return 'success';
    case 'review_pending':
      return 'warning';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status: DistroReviewStatus): string => {
  switch (status) {
    case 'review_pending':
      return 'Pending Review';
    case 'published':
      return 'Published';
    case 'closed':
      return 'Closed';
    case 'approved':
      return 'Approved';
    default:
      return status;
  }
};

export default function DistroReviewsList() {
  const history = useHistory();
  const L = useLocalizedStrings();
  const listDistroReviews = usePost('/distro-review/list');
  
  const [data, setData] = useState<DistroReviewsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [surveyNameFilter, setSurveyNameFilter] = useState<string>('');
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [debouncedGlobalSearch, setDebouncedGlobalSearch] = useState<string>('');
  const [sorting, setSorting] = useState<SortingState>([]);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = {
        page: currentPage,
        pageSize: pageSize,
      };
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (surveyNameFilter.trim()) {
        params.surveyName = surveyNameFilter.trim();
      }
      
      if (debouncedGlobalSearch.trim()) {
        params.search = debouncedGlobalSearch.trim();
      }
      
      if (sorting.length > 0) {
        params.sortBy = sorting[0].id;
        params.sortOrder = sorting[0].desc ? 'desc' : 'asc';
      }
      
      const result = await listDistroReviews(params);
      
      if ('error' in result) {
        setError(result.error);
        setData(null);
      } else {
        setData(result);
        setError(null);
      }
    } catch (err) {
      setError('Failed to load distro reviews');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, surveyNameFilter, debouncedGlobalSearch, sorting, listDistroReviews]);

  // Debounce global search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedGlobalSearch(globalSearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [globalSearch]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedGlobalSearch, statusFilter, surveyNameFilter, sorting]);

  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }: { pageIndex: number; pageSize: number }) => {
    const newPage = pageIndex + 1; // Convert from 0-based to 1-based
    
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(newPage);
    }
  };

  const handleRowClick = (review: DistroReviewListItem) => {
    history.push(`/distro-review/${review.id}`);
  };

  const columns: ColumnDef<DistroReviewListItem>[] = useMemo(() => [
    {
      id: 'id',
      header: 'Review #',
      accessorKey: 'id',
      cell: ({ row }) => (
        <Button
          variant="link"
          onClick={() => handleRowClick(row.original)}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          #{row.original.id}
        </Button>
      ),
      size: 100,
    },
    {
      id: 'title',
      header: 'Title',
      accessorKey: 'title',
      cell: ({ row }) => (
        <div className="max-w-xs truncate" title={row.original.title}>
          {row.original.title || ``}
        </div>
      ),
      size: 250,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => (
        <>
          <Badge variant={getStatusBadgeVariant(row.original.status)}>
            {getStatusLabel(row.original.status)}
          </Badge>
          {row.original.status === 'published' && (
            <div className="text-xs text-gray-500">
              by {row.original.publishedByName || row.original.publishedBy} on {row.original.publishedAt ? new Date(row.original.publishedAt).toLocaleString() : ''}
            </div>
          )}
          {row.original.status === 'closed' && (
            <div className="text-xs text-gray-500">
              by {row.original.closedByName || row.original.closedBy} on {row.original.closedAt ? new Date(row.original.closedAt).toLocaleString() : ''}
            </div>
          )}
        </>
      ),
      size: 150,
    },
    {
      id: 'surveyName',
      header: 'Survey',
      accessorKey: 'surveyName',
      cell: ({ row }) => (
        <div className="font-mono text-sm">
          {row.original.surveyName}
        </div>
      ),
      size: 200,
    },
    {
      id: 'createdBy',
      header: 'Created By',
      accessorKey: 'createdByName',
      cell: ({ row }) => (
        <div>
          {row.original.createdByName || row.original.createdBy}
        </div>
      ),
      size: 150,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {new Date(row.original.createdAt).toLocaleString()}
        </div>
      ),
      size: 180,
    },
  ], [history]);

  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Pending Review', value: 'review_pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Published', value: 'published' },
    { label: 'Closed', value: 'closed' },
  ];

  const filters = () => (
    <div className="flex gap-4 items-end">
      <div className="min-w-0 flex-1">
        <Input
          label="Survey Name"
          value={surveyNameFilter}
          onChange={(e) => setSurveyNameFilter(e.target.value)}
          placeholder="Filter by survey name..."
        />
      </div>
      <div className="min-w-0">
        <Select
          description="Filter reviews by status"
          label="Status"
          labelLocation="above"
          value={statusOptions.find(opt => opt.value === statusFilter) || statusOptions[0]}
          onChange={(selected) => setStatusFilter(selected.value)}
          options={statusOptions}
        />
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="p-6">
        <Heading level="h1" className="text-2xl font-bold mb-6">
          Distro Reviews
        </Heading>
        <Alert variant="danger" title="Error">
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Heading level="h1" className="text-2xl font-bold">
          Distro Reviews
        </Heading>
        <div className="text-sm text-gray-600">
          {data && (
            <>
              {data.totalCount} review{data.totalCount !== 1 ? 's' : ''} total
            </>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.reviews || []}
        initializing={loading}
        enableSearch={true}
        filters={filters}
        activeFilters={
          (statusFilter ? 1 : 0) + 
          (surveyNameFilter.trim() ? 1 : 0) + 
          (debouncedGlobalSearch.trim() ? 1 : 0)
        }
        pageSize={pageSize}
        showPagination={true}
        manualPagination={{
          pageCount: data?.totalPages,
          pageIndex: currentPage - 1, // Convert from 1-based to 0-based
          totalRows: data?.totalCount,
          onPaginationChange: handlePaginationChange,
        }}
        manualSorting={{
          sorting,
          onSortingChange: setSorting,
        }}
        manualSearch={{
          searchValue: globalSearch,
          onSearchChange: setGlobalSearch,
        }}
        className="bg-white rounded-lg shadow"
        dataFilteredOutMessage="No reviews match the current filters."
      />
    </div>
  );
}
